"use client"

import { useState, useEffect, useCallback, useMemo, JSX } from "react"
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useAutoRefresh } from "@/hooks/useAutoRefresh"
import { listArrayChildJobs } from "@/services/hpcJobsService"
import { HpcJob, JobStatus } from "@/types/hpc-jobs"
import {
  computeAggregateStatus,
  computeStatusCounts,
  formatCostUsd,
  formatJobDuration,
  formatJobTimestamp,
  getArrayChildIndex,
  getStatusVariant,
  isRunningStatus,
  sortChildJobsByIndex,
  transformJobData,
} from "@/lib/hpc-jobs-utils"
import { CancelJobButton } from "@/components/hpc-jobs/CancelJobButton"
import { DetailField } from "@/components/hpc-jobs/DetailField"
import TableFiltersSelector, { FilterOption } from "@/components/shared/TableFiltersSelector"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { CopyButton } from "@/components/ui/copy-button.tsx"
import { ArrowLeft, FileText, Loader2, RefreshCw } from "lucide-react"

/** Auto-refresh interval in seconds. */
const AUTO_REFRESH_INTERVAL = 15

/** Statuses at which a child task has reached execution and so has logs. */
const STATUSES_WITH_LOGS: JobStatus[] = [
  JobStatus.STARTING,
  JobStatus.RUNNING,
  JobStatus.SUCCEEDED,
  JobStatus.FAILED,
]

/** Order in which the status summary above the table lists statuses. */
const SUMMARY_STATUS_ORDER: JobStatus[] = [
  JobStatus.SUCCEEDED,
  JobStatus.FAILED,
  JobStatus.RUNNING,
  JobStatus.STARTING,
  JobStatus.RUNNABLE,
  JobStatus.PENDING,
  JobStatus.SUBMITTED,
]

/**
 * HPC Array Job detail page component.
 *
 * Lists the child tasks of an array job with each child's own status, timings,
 * cost and failure reason. The jobs overview aggregates child statuses into a
 * single value that can lag behind reality, so this page lists the parent's
 * child tasks directly and derives every summary shown here from those entries.
 *
 * Route params:
 *   - jobId: The parent array job ID (from URL path)
 *
 * Query params (all optional, for back-navigation):
 *   - tab, view: The overview tab/view to return to
 *   - from: "admin" when the user arrived from the admin view
 *   - fromWorkflow: the workflow ID when the user drilled in from a workflow
 *     page, so Back returns to that workflow rather than the overview
 */
export default function HpcArrayJobDetailPage(): JSX.Element {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getAccessToken } = useAuth()

  // Data state
  const [children, setChildren] = useState<HpcJob[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Active table filters keyed by filter (currently just "status"), each
  // mapping to the set of selected values. Page-local; not persisted to the URL.
  const [activeFilters, setActiveFilters] = useState<Map<string, Set<string>>>(new Map())

  /**
   * Fetches the array job's child tasks.
   *
   * The response contains one entry per child and does not include the parent
   * itself, so the parent's own details are read off the children further down.
   */
  const fetchChildren = useCallback(async (): Promise<void> => {
    if (!jobId) return

    setIsLoading(true)
    setError(null)

    try {
      const token = await getAccessToken()
      // Pricing is requested because the table shows a per-child cost column.
      const entries = await listArrayChildJobs({ jobId, includePrice: true }, token)

      setChildren(sortChildJobsByIndex(entries.map(transformJobData)))
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load child tasks"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [jobId, getAccessToken])

  // Fetch on mount
  useEffect(() => {
    fetchChildren()
  }, [fetchChildren])

  const { autoRefresh, setAutoRefresh, countdown } = useAutoRefresh({
    onRefresh: fetchChildren,
    intervalSeconds: AUTO_REFRESH_INTERVAL,
  })

  /**
   * Aggregate status across the child tasks, computed from the children
   * themselves rather than the parent's precomputed array_statuses.
   */
  const aggregateStatus = useMemo(
    (): JobStatus | null => (children.length > 0 ? computeAggregateStatus(children) : null),
    [children]
  )

  /** Per-status child counts shown in the summary above the table. */
  const statusCounts = useMemo(() => computeStatusCounts(children), [children])

  /**
   * Aggregated cost across the child tasks. Sums only children that carry
   * pricing data and stays undefined when none do, so a fully running array job
   * shows "-" rather than a misleading $0.00. Mirrors the workflow cost roll-up.
   */
  const totalUsd = useMemo((): number | undefined => {
    const priced: HpcJob[] = children.filter((child) => typeof child.totalUsd === "number")
    return priced.length > 0
      ? priced.reduce((sum, child) => sum + (child.totalUsd ?? 0), 0)
      : undefined
  }, [children])

  /**
   * Status filter options, limited to the statuses actually present among the
   * child tasks and ordered like the summary badges. Empty when there are no
   * children, which hides the filter control entirely.
   */
  const filterOptions = useMemo<FilterOption[]>(() => {
    const present: JobStatus[] = SUMMARY_STATUS_ORDER.filter((status) => statusCounts[status] > 0)
    return present.length > 0
      ? [{ key: "status", label: "Status", options: present }]
      : []
  }, [statusCounts])

  /**
   * Child tasks after applying the active status filter. With no status
   * selected every child shows; otherwise only children whose status is
   * selected. The array job's true size (children.length) is used elsewhere,
   * so filtering only changes which rows render.
   */
  const filteredChildren = useMemo<HpcJob[]>(() => {
    const statuses: Set<string> | undefined = activeFilters.get("status")
    if (!statuses || statuses.size === 0) return children
    return children.filter((child) => statuses.has(child.status))
  }, [children, activeFilters])

  /**
   * The array job's shared details. Every child carries the parent's name, user
   * and workflow, so the first child stands in for the parent.
   */
  const parent = children[0]

  /**
   * Handles back navigation. Returns to the originating workflow page when the
   * user drilled in from one (fromWorkflow), otherwise to whichever overview
   * they came from.
   */
  const handleBack = (): void => {
    const returnTab = searchParams.get("tab")
    const returnRange = searchParams.get("range")
    const from = searchParams.get("from")
    const fromWorkflow = searchParams.get("fromWorkflow")

    // The overview state params travel back to whichever page we return to.
    const params = new URLSearchParams()
    if (returnTab) params.set("tab", returnTab)
    if (returnRange) params.set("range", returnRange)

    // Drilled in from a workflow page: return there. from=admin is carried
    // forward so the workflow page's own Back button still lands on the admin
    // overview.
    if (fromWorkflow) {
      if (from === "admin") params.set("from", "admin")
      const query = params.toString()
      const workflowPath = `/computing/hpc/workflow/${encodeURIComponent(fromWorkflow)}`
      navigate(query ? `${workflowPath}?${query}` : workflowPath)
      return
    }

    const basePath = from === "admin" ? "/computing/hpc/admin" : "/computing/hpc"
    const queryString = params.toString()
    navigate(queryString ? `${basePath}?${queryString}` : basePath)
  }

  /**
   * Builds the logs link for a single child task.
   *
   * The logs page addresses array children by parent ID plus a child index, and
   * needs the array size to render its child selector. The number of children
   * returned here is that size.
   *
   * @param child - The child task to link to.
   * @returns A router path to the logs page with the child preselected.
   */
  function buildChildLogsHref(child: HpcJob): string {
    const params = new URLSearchParams({
      jobName: child.jobName,
      status: child.status,
      arraySize: String(children.length),
      childIndex: String(getArrayChildIndex(child.jobId) ?? 0),
    })

    const returnTab = searchParams.get("tab")
    const returnRange = searchParams.get("range")
    if (returnTab) params.set("tab", returnTab)
    if (returnRange) params.set("range", returnRange)
    if (searchParams.get("from") === "admin") params.set("from", "admin")

    return `/computing/hpc/logs/${encodeURIComponent(jobId ?? "")}?${params.toString()}`
  }

  /**
   * Builds the link to this array job's workflow page, carrying the overview
   * return params so the workflow page's Back button lands where the user
   * started.
   *
   * @param workflowId - The workflow ID to link to.
   * @returns A router path to the workflow page with preserved return params.
   */
  function buildWorkflowHref(workflowId: string): string {
    const params = new URLSearchParams()

    const returnTab = searchParams.get("tab")
    const returnRange = searchParams.get("range")
    if (returnTab) params.set("tab", returnTab)
    if (returnRange) params.set("range", returnRange)
    if (searchParams.get("from") === "admin") params.set("from", "admin")

    const queryString = params.toString()
    const path = `/computing/hpc/workflow/${encodeURIComponent(workflowId)}`

    return queryString ? `${path}?${queryString}` : path
  }

  return (
    <>
    <title>HPC Array Job - MAP Apps Platform</title>
    <div className="flex flex-1 flex-col min-h-0 overflow-y-auto p-6">
      <div className="flex flex-col gap-6 h-full">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold">Array Job</h1>
              {parent && (
                <span className="text-sm text-muted-foreground truncate max-w-xs" title={parent.jobName}>
                  {parent.jobName}
                </span>
              )}
              <div className="flex items-center gap-1">
                <CopyButton text={jobId || ""} />
                <code className="text-sm text-muted-foreground font-mono">{jobId}</code>
              </div>
              {aggregateStatus && (
                <Badge variant={getStatusVariant(aggregateStatus)}>{aggregateStatus}</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-refresh toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
                className="h-4 w-4"
              />
              Auto-refresh
              {autoRefresh && <span className="text-muted-foreground">({countdown}s)</span>}
            </label>
            {/* Manual refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchChildren()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => fetchChildren()} className="mt-2">
              Try Again
            </Button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && children.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Aggregated details, mirroring the job details panel on the logs page.
            Every child carries the parent's shared fields, so the first child
            (parent) stands in for them; the cost is rolled up across children. */}
        {parent && !error && (
          <div className="rounded-md border p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <DetailField label="Name">{parent.jobName}</DetailField>

              <DetailField label="Workflow ID">
                {parent.workflowId ? (
                  <div className="flex items-center gap-1 min-w-0">
                    <CopyButton text={parent.workflowId} />
                    <Link
                      to={buildWorkflowHref(parent.workflowId)}
                      className="font-mono text-xs truncate hover:underline"
                      title={parent.workflowName ?? parent.workflowId}
                    >
                      {parent.workflowId}
                    </Link>
                  </div>
                ) : (
                  "-"
                )}
              </DetailField>

              <DetailField label="User">{parent.user ?? "-"}</DetailField>

              <DetailField label="Total Cost">{formatCostUsd(totalUsd)}</DetailField>
            </div>
          </div>
        )}

        {/* Child tasks */}
        {(!isLoading || children.length > 0) && !error && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-medium">
                Child Tasks (
                {filteredChildren.length === children.length
                  ? children.length
                  : `${filteredChildren.length} of ${children.length}`}
                )
              </h2>

              <div className="flex items-center gap-3">
                {filterOptions.length > 0 && (
                  <TableFiltersSelector
                    filterOptions={filterOptions}
                    onSave={setActiveFilters}
                    initialFilters={activeFilters}
                  />
                )}

                {/* Cancelling the parent cancels every child, so this is only
                    offered while at least one child is still running. */}
                {aggregateStatus && isRunningStatus(aggregateStatus) && parent && jobId && (
                  <CancelJobButton
                    cancelId={jobId}
                    name={parent.jobName}
                    entityType="arrayJob"
                    showLabel
                    onCancelled={fetchChildren}
                  />
                )}
              </div>
            </div>

            {/* Per-status summary, counted from the child tasks themselves */}
            {children.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {SUMMARY_STATUS_ORDER.filter(status => statusCounts[status] > 0).map(status => (
                  <Badge key={status} variant={getStatusVariant(status)}>
                    {status}: {statusCounts[status]}
                  </Badge>
                ))}
              </div>
            )}

            {children.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                No child tasks found for this array job.
              </div>
            ) : filteredChildren.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                No child tasks match the current filters.
              </div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                {/* Every child is rendered; an array job with many thousands of
                    children will be slow to paint. If that becomes a problem,
                    virtualise the rows with @tanstack/react-virtual, which is
                    already a dependency. */}
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[7%]">Index</TableHead>
                      <TableHead className="w-[23%]">Job ID</TableHead>
                      <TableHead className="w-[11%]">Status</TableHead>
                      <TableHead className="w-[14%]">Start Time</TableHead>
                      <TableHead className="w-[14%]">End Time</TableHead>
                      <TableHead className="w-[11%]">Duration</TableHead>
                      <TableHead className="w-[8%] text-right">Cost</TableHead>
                      <TableHead className="w-[6%]">Logs</TableHead>
                      <TableHead className="w-[6%]"><span className="sr-only">Cancel</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredChildren.map(child => {
                      const childIndex = getArrayChildIndex(child.jobId)
                      const hasLogs = STATUSES_WITH_LOGS.includes(child.status)

                      // Surface the failure reason on the status badge rather
                      // than in a dedicated column, matching the jobs and
                      // workflow pages. Succeeded tasks carry no reason to show.
                      const statusTooltip: string | undefined =
                        child.status === JobStatus.FAILED && child.statusReason
                          ? child.statusReason
                          : undefined

                      return (
                        <TableRow key={child.jobId}>
                          <TableCell className="font-mono text-xs">
                            {childIndex ?? "-"}
                          </TableCell>
                          <TableCell className="overflow-hidden">
                            <div className="flex items-center gap-1 min-w-0">
                              <CopyButton text={child.jobId} />
                              <span className="font-mono text-xs truncate" title={child.jobId}>
                                {child.jobId}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusVariant(child.status)} title={statusTooltip}>
                              {child.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatJobTimestamp(child.startedAt)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatJobTimestamp(child.stoppedAt)}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatJobDuration(child.startedAt, child.stoppedAt)}
                          </TableCell>
                          <TableCell className="text-sm text-right whitespace-nowrap">
                            {formatCostUsd(child.totalUsd)}
                          </TableCell>
                          <TableCell>
                            {hasLogs && (
                              <Link to={buildChildLogsHref(child)}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="View logs"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </Link>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Only running child tasks can be cancelled. */}
                            {isRunningStatus(child.status) && (
                              <CancelJobButton
                                cancelId={child.jobId}
                                name={`${child.jobName}${childIndex !== undefined ? ` [${childIndex}]` : ""}`}
                                entityType="job"
                                onCancelled={fetchChildren}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  )
}
